import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { MagnifyingGlass, X, Download, Plus, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = [
  "all", "new", "reviewing", "contacted", "qualified",
  "consultation_requested", "consultation_booked", "won", "not_now", "closed",
];

// Human labels for sources we expect to see most often. Anything else falls back to the raw slug.
const SOURCE_LABELS = {
  contact: "Contact form",
  consultation: "Consultation",
  "co-innovation": "Co-innovation",
  co_innovation_general: "Co-innovation",
  interest_modal: "Interest modal",
  event_card: "Event card",
  promotion: "Promotion",
  voucher_vault: "VoucherVault demo",
  reflective_mind: "ReflectiveMind",
  walk_club: "WalkClub",
  unknown: "Unknown",
};

const labelFor = (s) => SOURCE_LABELS[s] || s.replace(/_/g, " ").replace(/(^|\s)\S/g, (c) => c.toUpperCase());

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]); // [{source, count}]
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // `${id}.invite` | `${id}.delete`

  const sendInvite = async (lead) => {
    if (!lead.email) { toast.error("This lead has no email on file."); return; }
    if (!window.confirm(`Send the IdeaLab discovery invite to ${lead.email}?`)) return;
    setRowBusy(`${lead.id}.invite`);
    try {
      await api.post(`/admin/leads/${lead.id}/send-idealab-invite`, { custom_note: "" });
      toast.success(`IdeaLab invite sent to ${lead.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send invite.");
    } finally {
      setRowBusy(null);
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete ${lead.name || lead.email}?\n\nThis removes the lead, all activity log entries and notes. It cannot be undone.`)) return;
    setRowBusy(`${lead.id}.delete`);
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead deleted");
      refreshLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete.");
    } finally {
      setRowBusy(null);
    }
  };

  const refreshLeads = () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (source !== "all") params.source = source;
    if (search) params.q = search;
    api.get("/admin/leads", { params }).then(({ data }) => setLeads(data || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, source, search]);

  // Source counts are independent of the current filter so the chips always show the full picture
  useEffect(() => {
    api.get("/admin/leads/sources").then(({ data }) => setSources(data || [])).catch(() => {});
  }, [leads.length]); // refresh whenever the leads list changes size

  const totalLeads = useMemo(() => sources.reduce((sum, s) => sum + (s.count || 0), 0), [sources]);

  return (
    <div className="p-8 md:p-10" data-testid="admin-leads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline mb-2">Lead management</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>All leads</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAddOpen(true)}
            className="btn-signal !py-2"
            data-testid="leads-add-btn"
          >
            <Plus size={14} weight="bold" /> Add lead
          </button>
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (status !== "all") params.set("status", status);
              if (source !== "all") params.set("source", source);
              if (search) params.set("q", search);
              window.open(`${API_BASE}/admin/leads.csv?${params.toString()}`, "_blank");
            }}
            className="btn-ghost-ink !py-2"
            data-testid="leads-export"
          >
            <Download size={14} weight="bold" /> Export CSV
          </button>
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="leads-count">{leads.length} shown</span>
        </div>
      </div>

      <AddLeadModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => { setAddOpen(false); refreshLeads(); }}
      />

      {/* Source filter chips */}
      {sources.length > 0 && (
        <div className="mb-4" data-testid="leads-source-chips">
          <div className="overline mb-2">Filter by source</div>
          <div className="flex flex-wrap gap-2">
            <SourceChip active={source === "all"} onClick={() => setSource("all")} testid="leads-source-all">
              All <span className="opacity-60 ml-1">{totalLeads}</span>
            </SourceChip>
            {sources.map((s) => (
              <SourceChip
                key={s.source}
                active={source === s.source}
                onClick={() => setSource(s.source)}
                testid={`leads-source-${s.source}`}
              >
                {labelFor(s.source)} <span className="opacity-60 ml-1">{s.count}</span>
              </SourceChip>
            ))}
            {source !== "all" && (
              <button
                onClick={() => setSource("all")}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 px-3 py-1.5"
                data-testid="leads-source-clear"
              >
                <X size={12} weight="bold" /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 mb-4 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 border border-zinc-200 px-3 py-2 flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, company…" className="flex-1 outline-none text-sm bg-transparent" data-testid="leads-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-zinc-200 px-3 py-2 text-sm bg-white" data-testid="leads-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Name / Email</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Company</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Source</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Created</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 font-mono">Loading…</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 font-mono">No leads match.</td></tr>
            ) : leads.map((l) => (
              <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/admin/leads/${l.id}`} className="font-medium hover:underline">{l.name || "—"}</Link>
                  <div className="text-xs text-zinc-500">{l.email}</div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{l.company || "—"}</td>
                <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{l.source || "—"}</td>
                <td className="px-4 py-3"><span className="status-pill">{(l.status || "new").replace(/_/g, " ")}</span></td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => sendInvite(l)}
                      disabled={!l.email || rowBusy === `${l.id}.invite`}
                      title="Send IdeaLab discovery invite"
                      className="p-1.5 text-zinc-500 hover:text-[hsl(var(--appistry-signal))] disabled:opacity-30"
                      data-testid={`lead-row-invite-${l.id}`}
                    >
                      <PaperPlaneTilt size={15} weight="fill" />
                    </button>
                    <button
                      onClick={() => removeLead(l)}
                      disabled={rowBusy === `${l.id}.delete`}
                      title="Delete lead"
                      className="p-1.5 text-zinc-500 hover:text-red-600 disabled:opacity-30"
                      data-testid={`lead-row-delete-${l.id}`}
                    >
                      <Trash size={15} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourceChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition-colors ${active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}
    >
      {children}
    </button>
  );
}

function AddLeadModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", source: "admin_added", notes: "" });
  const [sendInvite, setSendInvite] = useState(true);
  const [inviteNote, setInviteNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", company: "", phone: "", source: "admin_added", notes: "" });
      setSendInvite(true);
      setInviteNote("");
    }
  }, [open]);

  if (!open) return null;

  const canSubmit = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/leads", {
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        send_idealab_invite: sendInvite,
        invite_custom_note: inviteNote.trim(),
      });
      toast.success(
        data?.invite_sent
          ? `Lead added — IdeaLab invite sent to ${form.email}`
          : `Lead added`
      );
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-lead-modal"
    >
      <div
        className="bg-white border border-zinc-200 w-full max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 md:p-8 border-b border-zinc-200">
          <div>
            <p className="overline mb-1">New lead</p>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Add a lead you spoke to.
            </h2>
            <p className="text-sm text-zinc-600 mt-2">
              Captures the lead in the workbench and (optionally) emails them a direct IdeaLab link so they can start the discovery session in their own time.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-zinc-900 p-1" data-testid="add-lead-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Contact name" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-appistry"
                data-testid="add-lead-name"
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-appistry"
                data-testid="add-lead-email"
              />
            </Field>
            <Field label="Company">
              <input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="input-appistry"
                data-testid="add-lead-company"
              />
            </Field>
            <Field label="Telephone">
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-appistry"
                data-testid="add-lead-phone"
              />
            </Field>
            <Field label="Source">
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="admin_added, linkedin, referral, event…"
                className="input-appistry"
                data-testid="add-lead-source"
              />
            </Field>
          </div>

          <Field label="Internal notes">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Where you met, what you discussed, any context for the next conversation…"
              className="input-appistry"
              data-testid="add-lead-notes"
            />
          </Field>

          <div className="border border-zinc-200 bg-zinc-50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="mt-1"
                data-testid="add-lead-send-invite"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold inline-flex items-center gap-1.5">
                  <PaperPlaneTilt size={14} weight="fill" className="text-[hsl(var(--appistry-signal))]" />
                  Send them the IdeaLab discovery link
                </div>
                <div className="text-xs text-zinc-600 mt-1">
                  A short, personal email from Sam with a direct link to start the 11-step IdeaLab session. They walk away with a branded discovery pack.
                </div>
              </div>
            </label>
            {sendInvite && (
              <textarea
                rows={3}
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value)}
                placeholder="Optional one-liner to put at the very top of the email — context from your conversation. Leave blank for the generic 'Following on from our chat…' opener."
                className="input-appistry mt-3"
                data-testid="add-lead-invite-note"
              />
            )}
          </div>
        </div>

        <div className="px-6 md:px-8 py-5 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
          <button onClick={onClose} className="btn-ghost-ink !py-2" data-testid="add-lead-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit || saving}
            className="btn-signal disabled:opacity-40"
            data-testid="add-lead-submit"
          >
            {saving ? "Saving…" : sendInvite ? "Add lead & send invite" : "Add lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold text-zinc-800 mb-1.5">
        {label}{required && <span className="text-[hsl(var(--appistry-signal))] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
